import { lazy } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { seoHead, seoHeadFromPage } from "@/config/seoHead";
import { buildTitle } from "@/config/seoTitles";
import { scrapedPageQueryOptions } from "@/hooks/useScrapedPage";
import { lookupSlug, notFoundUnlessRedirected } from "@/lib/slugGuards";
import type { NewsArticle } from "@/data/newsArticles";

const NewsArticlePage = lazy(() => import("@/pages/NewsArticle"));

export const Route = createFileRoute("/nyheter/$slug")({
  // Nyheten kan ligga antingen i nyhetsarkivet eller bland de dynamiska sidorna.
  loader: async ({ params, context }) => {
    // useContent drar in de statiska nyhets- och rapportarkiven – ladda dem bara här.
    const { newsArticleQueryOptions, newsArticlesQueryOptions, neighbours } = await import("@/hooks/useContent");
    const path = `/nyheter/${params.slug}`;
    const [article, list] = await Promise.all([
      lookupSlug(() => context.queryClient.ensureQueryData(newsArticleQueryOptions(params.slug))),
      context.queryClient.ensureQueryData(newsArticlesQueryOptions()).catch(() => null),
    ]);
    if (article) {
      // Bara det som länkarna visar – hela arkivet behövs inte i HTML:en.
      const link = (a: NewsArticle | null) =>
        a ? { slug: a.slug, title: a.title, date: a.date, category: a.category, summary: a.summary } : null;
      const { newer, older } = neighbours(list, params.slug);
      const related = (list ?? [])
        .filter((a) => a.category === article.category && a.slug !== article.slug)
        .slice(0, 3)
        .map((a) => link(a)!);
      return { article, page: null, newer: link(newer), older: link(older), related };
    }
    const page = await lookupSlug(() => context.queryClient.ensureQueryData(scrapedPageQueryOptions(path)));
    // Finns varken i arkivet eller bland de dynamiska sidorna: riktig 404.
    // (undefined = uppslagningen misslyckades – rendera hellre än att ge en falsk 404.)
    if (article === null && page === null) await notFoundUnlessRedirected(path);
    return { article: null, page: page ?? null, newer: null, older: null, related: [] };
  },
  head: ({ params, loaderData }) => {
    const path = `/nyheter/${params.slug}`;
    if (loaderData?.article) {
      return seoHead(path, {
        title: buildTitle(loaderData.article.title, "article"),
        description: loaderData.article.summary,
        ogType: "article",
        ...(loaderData.article.imageUrl ? { image: loaderData.article.imageUrl } : {}),
        ...(loaderData.article.imageAlt ? { imageAlt: loaderData.article.imageAlt } : {}),
      });
    }
    return seoHeadFromPage(path, loaderData?.page, { ogType: "article" });
  },
  component: NewsRoute,
});

function NewsRoute() {
  const { article, page, newer, older, related } = Route.useLoaderData();
  return <NewsArticlePage article={article} page={page} newer={newer} older={older} related={related} />;
}
